import { API_URL } from "env";

import { mixLogOut } from '../services/mixin';
export interface AdInfo {
    ad_id?: string;
    ad_name?: string;
    ad_image?: string;
    ad_status?: string;
    creative_name?: string;
    // Các trường có trong ad_json (chi tiết hơn ad_info)
    campaign_id?: string;
    campaign_name?: string;
    adset_id?: string;
    adset_name?: string;
    post_id?: string;
    post_title?: string;
    post_content?: string;
    post_url?: string;
    [key: string]: any;
}

export interface ChannelMessageItem {
    id: number;
    direction: "inbound" | "outbound";
    text: string;
    // Ngoài các loại tài liệu liệt kê, dữ liệu thật còn có photo, sticker,
    // nosupport (tin nhắn kênh không hỗ trợ) -> để string cho khỏi sai kiểu
    message_type: "text" | "image" | "video" | "file" | "attachment" | string;
    payload: any;
    sent_at: string;
    ad_info?: AdInfo | null;
    ad_json?: AdInfo | null;
    /** Nhân viên gửi tin từ CMS. null: tin gửi từ công cụ khác (Meta Business Suite...) hoặc tin cũ */
    created_by_id?: number | null;
    /** Thông tin nhân viên gửi tin (theo created_by_id) */
    creator?: MessageCreator | null;
    /** Tên nhân viên gửi, dùng khi không có `creator` */
    staff_name?: string | null;
}

export interface MessageCreator {
    id: number;
    fullname?: string | null;
    name?: string | null;
    email?: string | null;
    avatar?: string | null;
}

/** Tên tài khoản trực page có dạng "Page- Tên" / "PAGE - Tên" -> chỉ giữ phần tên */
export function cleanStaffName(name?: string | null): string {
    return (name || "").replace(/^\s*page\s*-\s*/i, "").trim();
}

/** Tên nhân viên đã gửi tin/bình luận: ưu tiên object creator, thiếu thì lấy staff_name */
export function getMessageSenderName(item: { creator?: MessageCreator | null; staff_name?: string | null }): string {
    return cleanStaffName(item.creator?.fullname || item.creator?.name || item.staff_name);
}

export interface ChannelMessageListResponse {
    status: boolean;
    data: {
        items: ChannelMessageItem[];
        total: number;
    } | null;
}

export async function getChannelMessages(params: {
    customer_id: number;
    page?: number;
}): Promise<ChannelMessageListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        query.append('customer_id', String(params.customer_id));
        query.append('sort', 'created_at,asc');
        if (params.page) query.append('page', String(params.page));

        const response = await fetch(`${API_URL}/api/v1/be/channel-messages?${query.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as ChannelMessageListResponse;
    } catch (error) {
        console.error('Failed to fetch channel messages:', error);
        return {
            status: false,
            data: null,
        };
    }
}

export interface SendChannelMessageResponse {
    status: boolean;
    data: ChannelMessageItem | null;
}

export async function sendChannelMessage(params: {
    customer_id: number;
    text?: string;
    message_type: "text" | "image" | "video" | "file" | "audio";
    payload?: { url?: string;[key: string]: any };
}): Promise<SendChannelMessageResponse> {
    try {
        const token = localStorage.getItem('token') || '';

        const response = await fetch(`${API_URL}/api/v1/be/channel-message`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as SendChannelMessageResponse;
    } catch (error) {
        console.error('Failed to send channel message:', error);
        return {
            status: false,
            data: null,
        };
    }
}

export interface SendZaloOaMessageResponse {
    status: boolean;
    msg?: string;
    data?: any;
}

// Gửi tin nhắn Instagram (POST /api/v1/be/instagram-message)
// Dùng khi khách thuộc kết nối kênh có channel_type_code là 'insta' | 'instagram'.
// Hỗ trợ text và đính kèm (image/video/file/audio) qua payload.url.
export async function sendInstagramMessage(params: {
    customer_id: number;
    text?: string;
    message_type: string;
    payload?: { url: string };
}): Promise<SendZaloOaMessageResponse> {
    try {
        const token = localStorage.getItem('token') || '';

        const response = await fetch(`${API_URL}/api/v1/be/instagram-message`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                customer_id: params.customer_id,
                message_type: params.message_type,
                ...(params.text ? { text: params.text } : {}),
                ...(params.payload ? { payload: params.payload } : {}),
            }),
        });

        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg || json?.message };
        }
        return { status: json?.status ?? true, msg: json?.msg, data: json?.data };
    } catch (error) {
        console.error('Failed to send Instagram message:', error);
        return { status: false };
    }
}

// Gửi tin nhắn Zalo OA (POST) — KHÔNG còn dùng ở phòng chat: Zalo đã gửi chung
// qua sendChannelMessage như Facebook. Giữ lại để dự phòng nếu cần gọi riêng.
// Lưu ý: {id} trên URL là ID của channel_connections (Sender OA), KHÔNG phải oauth_accounts.
// API chỉ hỗ trợ nội dung văn bản (text).
export async function sendZaloOaMessage(params: {
    channel_connection_id: number;
    external_user_id: string;
    text: string;
}): Promise<SendZaloOaMessageResponse> {
    try {
        const token = localStorage.getItem('token') || '';

        const response = await fetch(`${API_URL}/api/v1/be/oauth-account/${params.channel_connection_id}/zalo-send-message`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                external_user_id: params.external_user_id,
                text: params.text,
                message_type: 'text',
            }),
        });

        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: extractZaloError(json) };
        }
        // API trả HTTP 200 kèm status=false khi lỗi nghiệp vụ/validate
        if (json?.status === false) {
            return { status: false, msg: extractZaloError(json) };
        }
        return { status: true, msg: json?.msg, data: json?.data };
    } catch (error) {
        console.error('Failed to send Zalo OA message:', error);
        return { status: false };
    }
}

/** Gom thông báo lỗi từ nhiều dạng response của backend (msg / message / error dạng object) */
function extractZaloError(json: any): string | undefined {
    if (!json) return undefined;
    if (typeof json.msg === 'string') return json.msg;
    if (typeof json.message === 'string') return json.message;
    if (json.error) {
        if (typeof json.error === 'string') return json.error;
        const first = Object.values(json.error)[0];
        if (typeof first === 'string') return first;
        if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
    }
    return undefined;
}
